<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="activeAccountTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(billingStartDate)="data">
        {{ data.value ? toDateWithYear(data.value) : "" }}
      </template>
      <template #cell(billingEndDate)="data">
        {{ data.value ? toDateWithYear(data.value) : "" }}
      </template>
      <template #cell(vat)="data">
        {{ data.value ? toBalance(data.value) : "" }}
      </template>
      <template #cell(discountAmount)="data">
        {{ toBalance(data.value) }}
      </template>
      <template #cell(subscriptionCharge)="data">
        {{ data.value ? toBalance(data.value) : "" }}
      </template>
      <template #cell(dataHandlingCharge)="data">
        {{ data.value ? toBalance(data.value) : "" }}
      </template>
      <template #cell(emailCharge)="data">
        {{ data.value ? toBalance(data.value) : "" }}
      </template>
      <template #cell(paymentStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <!-- Column: Actions -->
      <template #cell(action)="data">
        <div class="text-nowrap">
          <Button
            v-if="
              data.item.paymentStatus === 'Pending' ||
                data.item.paymentStatus === 'Failed'
            "
            :label="$t('Pay Now')"
            size="md"
            @click="payWithPaystack(data.item)"
          />
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="fetchInvoicePayment"
      @cancel="resetFilters"
    >
      <b-form>
        <b-row class="match-height">
          <!-- Reference Number -->
          <b-col md="6">
            <InputField
              id="reference-number"
              v-model="filter.referenceNumber"
              :label="$t('Reference Number')"
              :placeholder="$t('Enter Reference Number')"
              type="text"
              for="reference-number"
            />
          </b-col>
          <!-- Reference Number -->
          <b-col md="6">
            <SelectField
              id="payment-status"
              v-model="filter.paymentStatus"
              :label="$t('Payment Status')"
              :options="translatedPaymentStatusOptions"
              :placeholder="$t('Select')"
              :reduce="(option) => option.value"
              item="label"
            />
          </b-col>
        </b-row>
        <b-row class="match-height">
          <!-- Start Date -->
          <b-col md="6">
            <DatePickerField
              id="start-date"
              v-model="filter.startDate"
              :min-date="new Date()"
              :label="$t('Start Date')"
              :config="datePickerConfig"
              :placeholder="$t('Start Date')"
            />
          </b-col>
          <!-- Start Date -->
          <!-- End Date -->
          <b-col md="6">
            <DatePickerField
              id="end-date"
              v-model="filter.endDate"
              :min-date="new Date()"
              :label="$t('End Date')"
              :config="datePickerConfig"
              :placeholder="$t('End Date')"
            />
          </b-col>
          <!-- End Date -->
        </b-row>
      </b-form>
      <template
        v-if="isReadonly"
        #modal-footer="{}"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BBadge,
} from 'bootstrap-vue'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { toastAlert } from '@core/mixins/toast'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import PaystackPop from '@paystack/inline-js'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import getStatusColor from '@/helpers/getStatusColor'
import { toYearWithDate, toDateWithYear } from '@/helpers/formatDate'
import { toBalance } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import SelectField from '../../../forms/SelectField.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import { adminPermissions } from '@/helpers/permissions'

const paymentStatusOptions = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Paid', value: 'Paid' },
  { label: 'Failed', value: 'Failed' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BBadge,
    TableEntries,
    DatePickerField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin, toastAlert],
  data() {
    return {
      isReadonly: false,
      isBusy: false,
      toBalance,
      toDateWithYear,
      getStatusColor,
      required,
      sortBy: 'name',
      sortDesc: false,
      paymentRefreshTimeout: null,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'unitPrice', sortable: true, label: 'Unit Price' },
        { key: 'vat', sortable: true, label: 'VAT' },
        { key: 'discountAmount', sortable: true, label: 'Discount Amount' },
        {
          key: 'subscriptionCharge',
          sortable: true,
          label: 'Subscription Charge',
        },
        {
          key: 'dataHandlingCharge',
          sortable: true,
          label: 'Data Handling Charge',
        },
        { key: 'emailCharge', sortable: true, label: 'Email Charge' },
        {
          key: 'billableAccountsCount',
          sortable: true,
          label: 'Number of Accounts Count',
        },
        {
          key: 'transactionsCount',
          sortable: true,
          label: 'Transactions Count',
        },
        { key: 'paymentStatus', sortable: true, label: 'Payment Status' },
        {
          key: 'billingStartDate',
          sortable: true,
          label: 'Billing Start Date',
        },
        { key: 'billingEndDate', sortable: true, label: 'Billing End Date' },
        { key: 'action' },
      ],
      filter: {
        referenceNumber: '',
        startDate: null,
        endDate: null,
      },
      // paymentStatusOptions: ['Pending', 'Paid', 'Failed'],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: '',
        defaultDate: '',
      },
      reportData: [],
      invoiceDetails: {
        dataHandlingCharge: '',
        discountAmount: '',
        emailCharge: '',
        subscriptionCharge: '',
        vat: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedPaymentStatusOptions() {
      return paymentStatusOptions.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.value,
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchInvoicePayment()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchInvoicePayment()
    },
  },
  mounted() {
    this.fetchInvoicePayment()
  },
  beforeDestroy() {
    if (this.paymentRefreshTimeout) {
      clearTimeout(this.paymentRefreshTimeout)
      this.paymentRefreshTimeout = null
    }
  },
  methods: {
    ...mapActions({
      getInvoicePayment: 'admin/getSubscriptionPaymentHistory',
      initializePayment: 'admin/initializePaymentAction',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.startDate = null
      this.filter.endDate = null
      this.fetchInvoicePayment()
    },
    transactionReference() {
      let text = ''
      const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      for (let i = 0; i < 10; i += 1) text += possible.charAt(Math.floor(Math.random() * possible.length))
      return text
    },

    async payWithPaystack(items) {
      try {
        const formData = {
          paymentReference: items.referenceNumber,
        }
        const response = await this.initializePayment({
          formData,
          permission: adminPermissions.VIEW_SUBSCRIPTION_INVOICE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          const accessCode = data.data.access_code
          const popup = new PaystackPop()
          // Configure the popup with callbacks
          popup.resumeTransaction(accessCode, {
            onSuccess: () => {
              // Payment was successful
              this.alertSuccess({ text: 'Payment completed successfully' })

              if (this.paymentRefreshTimeout) {
                clearTimeout(this.paymentRefreshTimeout)
              }

              // Refresh the table data
              this.paymentRefreshTimeout = setTimeout(() => {
                this.fetchInvoicePayment()
                this.paymentRefreshTimeout = null
              }, 1000)
            },
            onCancel: () => {
              // User cancelled the payment
              console.log('Payment cancelled')
              this.toastAlert({
                title: 'Cancelled',
                icon: 'XIcon',
                variant: 'warning',
                text: 'Payment was cancelled',
              }).catch(() => {})
            },
            onError: error => {
              // Payment failed
              console.error('Payment error:', error)
              this.alertError({ text: 'Payment failed. Please try again.' })
            },
          })
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    async fetchInvoicePayment() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const filteredData = {
          ...this.filter,
          startDate:
            this.filter.startDate !== null
              ? toYearWithDate(this.filter.startDate)
              : '',
          endDate:
            this.filter.endDate !== null
              ? toYearWithDate(this.filter.endDate)
              : '',
        }
        const response = await this.getInvoicePayment({
          search: filteredData,
          page: this.currentPage,
          limit: this.perPage,
          permission: adminPermissions.VIEW_SUBSCRIPTION_INVOICE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.totalItems
          this.isBusy = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.invoice-filter-select {
  min-width: 190px;
  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }
  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
